/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useVoicemailSettingsData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing Voicemail Settings data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  VoicemailSettingsFormData,
  DEFAULT_FORM_DATA,
  VoicemailSettingsPayload,
} from '../types';
import {
  fetchVoicemailSettings,
  createVoicemailSettings,
  updateVoicemailSettings,
} from '../api';

/**
 * Options for the useVoicemailSettingsData hook.
 */
interface UseVoicemailSettingsDataOptions {
  /** Callback fired upon successful load or save, receiving a success message. */
  onSuccess?: (message: string) => void;
  /** Callback fired upon failure, receiving the Error object. */
  onError?: (error: Error) => void;
}

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

/** Shape of the API response object used to map settings to the form. */
interface VoicemailSettingsApiResponse {
  id?: number;
  /** Raw settings bag from the backend — values can be strings, numbers, or booleans */
  settings?: Record<string, unknown>;
}

const mapSettingsToForm = (settings: VoicemailSettingsApiResponse): VoicemailSettingsFormData => {
  return {
    maxMessageLength: Number(settings?.settings?.max_message_length ?? DEFAULT_FORM_DATA.maxMessageLength),
    minMessageLength: Number(settings?.settings?.min_message_length ?? DEFAULT_FORM_DATA.minMessageLength),
    greetingsLength: Number(settings?.settings?.greetings_length ?? DEFAULT_FORM_DATA.greetingsLength),
    maxSilence: Number(settings?.settings?.max_silence ?? DEFAULT_FORM_DATA.maxSilence),
    maxLoginAttempts: Number(settings?.settings?.max_login_attempts ?? DEFAULT_FORM_DATA.maxLoginAttempts),
    backupDeleted: Number(settings?.settings?.backup_deleted ?? DEFAULT_FORM_DATA.backupDeleted),
    maxMessages: Number(settings?.settings?.max_messages ?? DEFAULT_FORM_DATA.maxMessages),
    locale: typeof settings?.settings?.locale === 'string' ? settings.settings.locale : DEFAULT_FORM_DATA.locale,
    moveHeardMsg: typeof settings?.settings?.move_heard_msg === 'boolean' ? settings.settings.move_heard_msg : settings?.settings?.move_heard_msg === 'true',
    forceName: typeof settings?.settings?.force_name === 'boolean' ? settings.settings.force_name : settings?.settings?.force_name === 'true',
    forceGreetings: typeof settings?.settings?.force_greetings === 'boolean' ? settings.settings.force_greetings : settings?.settings?.force_greetings === 'true',
    useDirectory: typeof settings?.settings?.use_directory === 'boolean' ? settings.settings.use_directory : settings?.settings?.use_directory === 'true',
    reviewMsg: typeof settings?.settings?.review_msg === 'boolean' ? settings.settings.review_msg : settings?.settings?.review_msg === 'true',
    emailDateFormat: typeof settings?.settings?.email_date_format === 'string' ? settings.settings.email_date_format : DEFAULT_FORM_DATA.emailDateFormat,
  };
};

/**
 * Custom React hook for managing Voicemail Settings data.
 * Handles fetching settings from the API, form state management, tracking changes,
 * and saving (create/update) the settings.
 * @param options - Configuration options for the hook.
 */
export function useVoicemailSettingsData(options: UseVoicemailSettingsDataOptions = {}) {
  const { onSuccess, onError } = options;

  // Use refs to store callbacks to prevent infinite loops
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const [formData, setFormData] = useState<VoicemailSettingsFormData>(deepClone(DEFAULT_FORM_DATA));
  const [originalData, setOriginalData] = useState<VoicemailSettingsFormData>(deepClone(DEFAULT_FORM_DATA));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Check if there are unsaved changes
  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData],
  );

  // Load voicemail settings
  const loadVoicemailSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchVoicemailSettings();

      if (result.success && result.data) {
        const mapped = mapSettingsToForm(result.data);
        setFormData(mapped);
        setOriginalData(deepClone(mapped));
        setHasExisting(Boolean(result.data?.id));
        setErrors({});
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load voicemail settings');
      setError(error);
      setFormData(deepClone(DEFAULT_FORM_DATA));
      setOriginalData(deepClone(DEFAULT_FORM_DATA));
      setHasExisting(false);
      onErrorRef.current?.(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save voicemail settings
  const saveVoicemailSettings = useCallback(
    async (): Promise<void> => {
      try {
        setIsSaving(true);
        setError(null);

        const payload: VoicemailSettingsPayload = {
          settings: {
            maxMessageLength: formData.maxMessageLength,
            minMessageLength: formData.minMessageLength,
            greetingsLength: formData.greetingsLength,
            maxSilence: formData.maxSilence,
            maxLoginAttempts: formData.maxLoginAttempts,
            backupDeleted: formData.backupDeleted,
            maxMessages: formData.maxMessages,
            locale: formData.locale,
            moveHeardMsg: formData.moveHeardMsg,
            forceName: formData.forceName,
            forceGreetings: formData.forceGreetings,
            useDirectory: formData.useDirectory,
            reviewMsg: formData.reviewMsg,
            emailDateFormat: formData.emailDateFormat.trim(),
          },
        };

        let result;
        if (hasExisting) {
          result = await updateVoicemailSettings(payload);
        } else {
          result = await createVoicemailSettings(payload);
        }

        if (result.success && result.data) {
          const mapped = mapSettingsToForm(result.data);
          setFormData(mapped);
          setOriginalData(deepClone(mapped));
          setHasExisting(true);
          onSuccessRef.current?.(
            hasExisting
              ? 'Voicemail settings updated successfully'
              : 'Voicemail settings created successfully'
          );
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to save voicemail settings');
        setError(error);
        onErrorRef.current?.(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasExisting]
  );

  // Handle field change
  const handleFieldChange = useCallback(<K extends keyof VoicemailSettingsFormData>(
    field: K,
    value: VoicemailSettingsFormData[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  }, [errors]);

  // Reset to original
  const resetToOriginal = useCallback(() => {
    setFormData(deepClone(originalData));
    setErrors({});
  }, [originalData]);

  // Load data on mount
  useEffect(() => {
    loadVoicemailSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    formData,
    originalData,
    errors,
    isLoading,
    isSaving,
    hasExisting,
    hasChanges,
    error,
    loadVoicemailSettings,
    saveVoicemailSettings,
    handleFieldChange,
    resetToOriginal,
    setErrors,
  };
}

